import { useEffect, useState } from 'react'
import { Avatar, Card, Typography, Form, Input, Button, message, Modal, Space, QRCode, Upload } from 'antd'
import { CopyOutlined, DeleteOutlined, LoadingOutlined, SafetyCertificateOutlined, UploadOutlined } from '@ant-design/icons'
import { useMerchantStore } from '../../stores/merchantStore'
import { getMerchant, updateMerchant as saveMerchantSettings, uploadMerchantLogo } from '../../services/merchant'
import { changePassword, createTwoFactorSetup, enableTwoFactor, sendTwoFactorRebindCode } from '../../services/auth'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'
import { useAuthStore } from '../../stores/authStore'

const { Title } = Typography
const { TextArea } = Input
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024
const LOGO_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const LOGO_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp']

export default function Settings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [logoUploading, setLogoUploading] = useState(false)
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [twoFactorOpen, setTwoFactorOpen] = useState(false)
  const [twoFactorLoading, setTwoFactorLoading] = useState(false)
  const [rebindCodeSending, setRebindCodeSending] = useState(false)
  const [setupSecret, setSetupSecret] = useState<string | null>(null)
  const [setupOtpauthUrl, setSetupOtpauthUrl] = useState<string | null>(null)
  const [setupToken, setSetupToken] = useState<string | null>(null)
  const { merchant, setMerchant, updateMerchant: updateMerchantState } = useMerchantStore()
  const emailVerification = useEmailCodeVerification(`rebind:${merchant?.id ?? ''}:${twoFactorOpen}`)
  const updateTwoFactorEnabled = useAuthStore((state) => state.updateTwoFactorEnabled)
  const [form] = Form.useForm()
  const logoPreview = Form.useWatch('logo', form) as string | undefined
  const [passwordForm] = Form.useForm<{ currentPassword: string; newPassword: string; confirmPassword: string; totpCode?: string }>()
  const [twoFactorForm] = Form.useForm<{ emailCode?: string; totpCode?: string }>()
  const websiteLocked = merchant?.kycStatus === 'APPROVED' || merchant?.kybStatus === 'APPROVED'

  useEffect(() => {
    getMerchant()
      .then((res) => {
        setMerchant(res.data)
        form.setFieldsValue({
          name: res.data.name,
          logo: res.data.logo || '',
          website: res.data.website,
          description: res.data.description,
          phone: res.data.phone
        })
      })
      .finally(() => setLoading(false))
  }, [form, setMerchant])

  const handleSubmit = async (values: { name?: string; logo?: string; website?: string; description?: string; phone?: string }) => {
    setSaving(true)
    try {
      const payload = { ...values }
      if (websiteLocked) delete payload.website
      const res = await saveMerchantSettings(payload)
      setMerchant(res.data)
      message.success('設定已保存')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '保存失敗')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (file: File) => {
    const filename = file.name.toLowerCase()
    const hasAllowedExtension = LOGO_EXTENSIONS.some((extension) => filename.endsWith(extension))
    const hasAllowedType = !file.type || LOGO_MIME_TYPES.includes(file.type.toLowerCase())
    if (!hasAllowedExtension || !hasAllowedType) {
      message.error('Logo 僅支援 JPG、PNG 或 WebP')
      return false
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      message.error('Logo 檔案不可超過 2MB')
      return false
    }

    setLogoUploading(true)
    try {
      const res = await uploadMerchantLogo(file)
      form.setFieldValue('logo', res.data.url)
      message.success('Logo 上傳成功')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : 'Logo 上傳失敗')
    } finally {
      setLogoUploading(false)
    }
    return false
  }

  const merchantInitial = merchant?.name?.trim().charAt(0).toUpperCase() || 'M'

  const handlePasswordChange = async (values: { currentPassword: string; newPassword: string; confirmPassword: string; totpCode?: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      passwordForm.setFields([{ name: 'confirmPassword', errors: ['兩次輸入的密碼不一致'] }])
      return
    }
    setPasswordSaving(true)
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        totpCode: values.totpCode,
      })
      passwordForm.resetFields()
      message.success('密碼已更新')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '修改密碼失敗')
    } finally {
      setPasswordSaving(false)
    }
  }

  const openTwoFactorModal = () => {
    emailVerification.reset()
    twoFactorForm.resetFields()
    setSetupSecret(null)
    setSetupOtpauthUrl(null)
    setSetupToken(null)
    setTwoFactorOpen(true)
  }

  const handleSendRebindCode = async () => {
    setRebindCodeSending(true)
    try {
      await emailVerification.send((token) => sendTwoFactorRebindCode(token))
      message.success('郵箱驗證碼已發送')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setRebindCodeSending(false)
    }
  }

  const handleCreateTwoFactorSetup = async () => {
    if (merchant?.twoFactorEnabled && !emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    setTwoFactorLoading(true)
    try {
      await twoFactorForm.validateFields(['emailCode'])
      const res = await createTwoFactorSetup(twoFactorForm.getFieldValue('emailCode'))
      setSetupSecret(res.data.secret)
      setSetupOtpauthUrl(res.data.otpauthUrl)
      setSetupToken(res.data.setupToken ?? null)
      message.success(merchant?.twoFactorEnabled ? '新的 2FA 設定已生成' : '2FA 設定已生成')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '生成 2FA 失敗')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const handleEnableTwoFactor = async () => {
    if (!setupSecret) return
    setTwoFactorLoading(true)
    try {
      await enableTwoFactor({
        secret: setupSecret,
        totpCode: twoFactorForm.getFieldValue('totpCode'),
        setupToken: setupToken ?? undefined,
      })
      updateTwoFactorEnabled(true)
      updateMerchantState({ twoFactorEnabled: true })
      setTwoFactorOpen(false)
      setSetupSecret(null)
      setSetupOtpauthUrl(null)
      setSetupToken(null)
      twoFactorForm.resetFields()
      message.success(merchant?.twoFactorEnabled ? '2FA 已重新設定' : '2FA 已啟用')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '啟用 2FA 失敗')
    } finally {
      setTwoFactorLoading(false)
    }
  }

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text)
    message.success('已複製')
  }

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>賬戶設定</Title>

      <Card title="商戶資訊" style={{ maxWidth: 600 }} loading={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="logo" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label={(
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 8px' }}>
                <span>商戶 Logo</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  JPG、PNG 或 WebP，最大 2MB，建議 1:1、512 × 512px
                </Typography.Text>
              </span>
            )}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 80 }}>
              <Avatar
                shape="square"
                size={80}
                src={logoPreview || undefined}
                alt="商戶 Logo"
                style={{ flex: '0 0 80px', border: '1px solid #d9d9d9', background: '#f0f5ff', color: '#1677ff', fontWeight: 700 }}
              >
                {merchantInitial}
              </Avatar>
              <Space wrap>
                <Upload
                  accept={LOGO_EXTENSIONS.join(',')}
                  showUploadList={false}
                  beforeUpload={handleLogoUpload}
                  disabled={logoUploading}
                >
                  <Button
                    icon={logoUploading ? <LoadingOutlined /> : <UploadOutlined />}
                    loading={logoUploading}
                    disabled={logoUploading}
                  >
                    {logoPreview ? '替換 Logo' : '上傳 Logo'}
                  </Button>
                </Upload>
                {logoPreview && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={logoUploading}
                    onClick={() => form.setFieldValue('logo', '')}
                  >
                    刪除
                  </Button>
                )}
              </Space>
            </div>
          </Form.Item>
          <Form.Item label="郵箱">
            <Input value={merchant?.email} disabled />
          </Form.Item>
          <Form.Item label="商戶名稱">
            <Input value={merchant?.name || ''} disabled />
          </Form.Item>
          <Form.Item
            name="website"
            label="網站"
            extra={websiteLocked ? 'KYC 或 KYB 已通過，如需修改網站請聯絡管理員。' : undefined}
          >
            <Input placeholder="https://your-website.com" disabled={websiteLocked} />
          </Form.Item>
          <Form.Item name="description" label="商戶描述">
            <TextArea rows={4} placeholder="簡單介紹您的業務" />
          </Form.Item>
          <Form.Item name="phone" label="電話號碼" rules={[{ pattern: /^\s*$|^\s*\+?[0-9][0-9 ()-]{4,30}[0-9]\s*$/, message: '請輸入有效的電話號碼' }]}>
            <Input type="tel" maxLength={40} placeholder="+852 1234 5678" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} disabled={logoUploading}>儲存</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="雙重驗證" style={{ maxWidth: 600, marginTop: 24 }}>
        <Typography.Paragraph>
          {merchant?.twoFactorEnabled ? '目前已開啟 TOTP 雙重驗證。' : '開啟 TOTP 雙重驗證後，登入與敏感操作都需要 6 位驗證碼。'}
        </Typography.Paragraph>
        <Button icon={<SafetyCertificateOutlined />} onClick={openTwoFactorModal}>
          {merchant?.twoFactorEnabled ? '修改 2FA' : '建立 2FA'}
        </Button>
      </Card>

      <Card title="修改登入密碼" style={{ maxWidth: 600, marginTop: 24 }}>
        <Form form={passwordForm} layout="vertical" onFinish={handlePasswordChange}>
          <Form.Item name="currentPassword" label="目前密碼" rules={[{ required: true, message: '請輸入目前密碼' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密碼"
            rules={[{ required: true, message: '請輸入新密碼' }, { min: 8, message: '密碼至少 8 位' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="confirmPassword" label="確認新密碼" rules={[{ required: true, message: '請再次輸入新密碼' }]}>
            <Input.Password />
          </Form.Item>
          {merchant?.twoFactorEnabled && (
            <Form.Item name="totpCode" label="2FA 驗證碼" rules={[{ required: true, message: '請輸入 2FA 驗證碼' }]}>
              <Input maxLength={6} />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" loading={passwordSaving}>更新密碼</Button>
        </Form>
      </Card>

      <Modal
        title={merchant?.twoFactorEnabled ? '修改 2FA' : '建立 2FA'}
        open={twoFactorOpen}
        onCancel={() => setTwoFactorOpen(false)}
        footer={null}
      >
        <Form form={twoFactorForm} layout="vertical">
          {twoFactorOpen && merchant?.twoFactorEnabled && !setupSecret && (
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 16 }}>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                修改 2FA 前，需先通過寄送到 {merchant?.email} 的郵箱驗證碼。
              </Typography.Paragraph>
              <Button onClick={handleSendRebindCode} loading={rebindCodeSending} disabled={!emailVerification.canSend}>
                發送郵箱驗證碼
              </Button>
              <Form.Item
                name="emailCode"
                label="郵箱驗證碼"
                rules={[{ required: true, message: '請輸入郵箱驗證碼' }]}
              >
                <Input maxLength={6} />
              </Form.Item>
            </Space>
          )}
          {twoFactorOpen && merchant?.twoFactorEnabled && !setupSecret && (
            <EmailCodeVerification verification={emailVerification} />
          )}
          {!setupSecret ? (
            <Button type="primary" onClick={handleCreateTwoFactorSetup} loading={twoFactorLoading} disabled={Boolean(merchant?.twoFactorEnabled && !emailVerification.ready)}>
              生成新的 2FA 設定
            </Button>
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                請使用驗證器 App 掃描下方 QR Code；若無法掃描，也可手動輸入 Secret。
              </Typography.Paragraph>
              {setupOtpauthUrl && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                  <QRCode value={setupOtpauthUrl} size={208} />
                </div>
              )}
              <Input
                readOnly
                value={setupSecret}
                addonAfter={<Button type="link" icon={<CopyOutlined />} onClick={() => copyText(setupSecret)}>複製</Button>}
              />
              {setupOtpauthUrl && (
                <Input
                  readOnly
                  value={setupOtpauthUrl}
                  addonAfter={<Button type="link" icon={<CopyOutlined />} onClick={() => copyText(setupOtpauthUrl)}>複製</Button>}
                />
              )}
              <Form.Item
                name="totpCode"
                label="新的 2FA 驗證碼"
                rules={[{ required: true, message: '請輸入新的 2FA 驗證碼' }]}
              >
                <Input maxLength={6} />
              </Form.Item>
              <Button type="primary" onClick={handleEnableTwoFactor} loading={twoFactorLoading}>
                完成 {merchant?.twoFactorEnabled ? '修改' : '啟用'}
              </Button>
            </Space>
          )}
        </Form>
      </Modal>
    </div>
  )
}
